import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authForgot, authLogin, authRequestLink } from "@/lib/api";
import { useSpark } from "@/lib/store";
import { BrandHeader } from "@/components/BrandHeader";
import { PublicFooter } from "@/components/PublicFooter";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map(e => e?.msg).filter(Boolean).join(" ") : "Something went wrong. Please try again.");
const inputCls = "w-full rounded-xl border bg-white px-4 py-3.5 text-base focus:outline-none focus:ring-2 transition-shadow";
const inputStyle = { borderColor: "hsl(var(--input))", "--tw-ring-color": "rgba(44,80,160,0.35)" };

export default function SignIn() {
  const nav = useNavigate();
  const { setSession, switchUser } = useSpark();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("password"); // password | link | forgot
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submitPassword = async (e) => {
    e.preventDefault();
    setState("sending"); setError("");
    try {
      const { user } = await authLogin(email.trim(), password);
      setSession(user); switchUser(user.user_id);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(errText(err?.response?.data?.detail)); setState("idle");
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending"); setError("");
    try { await authForgot(email.trim()); setState("forgot-sent"); } catch { setError("Something interrupted us. Please try again."); setState("idle"); }
  };

  const submitLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending"); setError("");
    try { await authRequestLink(email.trim()); setState("sent"); } catch { setError("Something interrupted us. Please try again."); setState("idle"); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader links={[{ label: "How it works", href: "/#how" }, { label: "Privacy", to: "/welcome-note?review=1" }]} />
      <main className="flex-1" style={{ background: "hsl(var(--spark-cool))" }}>
        <div className="spark-narrow py-20 sm:py-28">
          <div data-testid="signin-card" className="spark-card mx-auto max-w-xl px-7 py-10 sm:px-12 sm:py-14">
            <img src="/brand/monogram_black.png" alt="" aria-hidden="true" className="h-14 w-14" />
            <p className="spark-eyebrow-copper mt-8">Sign in</p>
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Welcome back.</h1>

            {mode === "password" && (
              <form onSubmit={submitPassword} className="mt-8" data-testid="signin-password-form">
                <label className="spark-eyebrow block mb-2" htmlFor="signin-email">Your email</label>
                <input id="signin-email" data-testid="signin-email-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputCls} style={inputStyle} placeholder="you@example.com" />
                <label className="spark-eyebrow block mt-5 mb-2" htmlFor="signin-password">Password</label>
                <input id="signin-password" data-testid="signin-password-input" type="password" required value={password} onChange={e => setPassword(e.target.value)} className={inputCls} style={inputStyle} placeholder="Your password" />
                <button data-testid="signin-submit-btn" disabled={state === "sending"} className="spark-btn-primary mt-6 w-full sm:w-auto disabled:opacity-60">
                  {state === "sending" ? "Signing in…" : "Sign in"}
                </button>
                {error && <p data-testid="signin-error" className="mt-3 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}
                <p className="mt-6 text-sm" style={{ color: "hsl(var(--spark-ink-soft))" }}>
                  <button type="button" data-testid="signin-forgot-btn" onClick={() => { setMode("forgot"); setError(""); }} className="font-medium underline underline-offset-2 text-[#2C50A0]">Forgot password?</button>
                  <span className="mx-2">·</span>
                  Part of the pilot?{" "}
                  <button type="button" data-testid="signin-use-link-btn" onClick={() => { setMode("link"); setError(""); }} className="font-medium underline underline-offset-2 text-[#2C50A0]">Email me a sign-in link</button>
                </p>
              </form>
            )}

            {mode === "link" && state !== "sent" && (
              <form onSubmit={submitLink} className="mt-8" data-testid="signin-link-form">
                <p className="spark-voice text-lg sm:text-xl leading-snug" style={{ color: "hsl(var(--spark-ink-soft))" }}>
                  We'll send a link that signs you in — it works once, then expires.
                </p>
                <label className="spark-eyebrow block mt-7 mb-2" htmlFor="signin-link-email">Your email</label>
                <input id="signin-link-email" data-testid="signin-link-email-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputCls} style={inputStyle} placeholder="you@example.com" />
                <button data-testid="signin-link-submit-btn" disabled={state === "sending"} className="spark-btn-primary mt-5 w-full sm:w-auto disabled:opacity-60">
                  {state === "sending" ? "Sending…" : "Email me a sign-in link"}
                </button>
                {error && <p data-testid="signin-error" className="mt-3 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}
                <p className="mt-6 text-sm" style={{ color: "hsl(var(--spark-ink-soft))" }}>
                  <button type="button" data-testid="signin-use-password-btn" onClick={() => { setMode("password"); setError(""); }} className="font-medium underline underline-offset-2 text-[#2C50A0]">Use my password instead</button>
                </p>
              </form>
            )}

            {mode === "forgot" && state !== "forgot-sent" && (
              <form onSubmit={submitForgot} className="mt-8" data-testid="signin-forgot-form">
                <p className="spark-voice text-lg sm:text-xl leading-snug" style={{ color: "hsl(var(--spark-ink-soft))" }}>
                  We'll email you a link to choose a new password. It works once and expires in 30 minutes.
                </p>
                <label className="spark-eyebrow block mt-7 mb-2" htmlFor="signin-forgot-email">Your email</label>
                <input id="signin-forgot-email" data-testid="signin-forgot-email-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputCls} style={inputStyle} placeholder="you@example.com" />
                <button data-testid="signin-forgot-submit-btn" disabled={state === "sending"} className="spark-btn-primary mt-5 w-full sm:w-auto disabled:opacity-60">
                  {state === "sending" ? "Sending…" : "Email me a reset link"}
                </button>
                {error && <p data-testid="signin-error" className="mt-3 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}
                <p className="mt-6 text-sm" style={{ color: "hsl(var(--spark-ink-soft))" }}>
                  <button type="button" data-testid="signin-use-password-btn-2" onClick={() => { setMode("password"); setError(""); }} className="font-medium underline underline-offset-2 text-[#2C50A0]">Back to sign in</button>
                </p>
              </form>
            )}

            {state === "forgot-sent" && (
              <div data-testid="signin-forgot-sent" className="mt-8 spark-voice-block">
                <p className="spark-voice text-xl sm:text-2xl leading-snug">If that email belongs to a Spark account, a reset link is on its way.</p>
                <p className="mt-3 text-sm sm:text-base" style={{ color: "hsl(var(--spark-ink-soft))" }}>Check your inbox (and spam folder). The link works once and expires in 30 minutes.</p>
              </div>
            )}

            {state === "sent" && (
              <div data-testid="signin-sent" className="mt-8 spark-voice-block">
                <p className="spark-voice text-xl sm:text-2xl leading-snug">If that email belongs to a Spark account, a sign-in link is on its way.</p>
                <p className="mt-3 text-sm sm:text-base" style={{ color: "hsl(var(--spark-ink-soft))" }}>During the private pilot your coach may send the link personally — by email, text or WhatsApp. It works once and expires within 30 minutes of being created.</p>
              </div>
            )}

            <p className="mt-10 pt-5 text-sm spark-rule" style={{ color: "hsl(var(--spark-ink-soft))" }}>
              New to Spark? <Link to="/start" data-testid="signin-start-link" className="font-medium underline underline-offset-2 text-[#2C50A0]">Find my next step — A$99</Link>
            </p>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
